/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { Location } from 'history';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import cn from 'classnames';

import { IRootState } from 'src/redux/rootReducer';
import { View, Button, Text, Image, InputPassword, IconSuccess } from 'src/components/common';
import Form from 'src/components/common/Form';
import Footer from 'src/components/Footer';
import ComfirmationCodeField from 'src/components/ComfirmationCodeField';

import { IMAGES } from 'src/appConfig/images';
import { PATHS } from 'src/appConfig/paths';
import { getLocationState } from 'src/utils';
import { useComponentDidMount } from 'src/hooks';
import { ErrorService, Navigator, Yup } from 'src/services';
import modalActions from 'src/redux/modal/actions';
import { forgotPasswordAsync, signInAsync, submitForgotPasswordAsync } from 'src/redux/auth/actions';
import { MODAL_TYPES } from 'src/appConfig/modal';

type FormValue = {
  token: string;
  password: string;
};

const INTIAL = { token: '', password: '' };

const ResetPassword: React.FC<Props> = ({
  error,
  location,
  isResetPasswordSuccess,
  loading,
  onSignIn,
  onShowModal,
  onCloseModal,
  onResendCode,
  onResetPassword,
}) => {
  const formRef = useRef<FormikProps<FormValue>>(null);

  const email = useRef<string>('');

  const state = getLocationState(location);
  useComponentDidMount(() => {
    if (!state?.email) {
      Navigator.navigate(PATHS.forgotPassword);
      return '';
    }

    email.current = state.email as string;
  });

  useEffect(() => {
    if (error) handleError(error);
  }, [error]);

  useEffect(() => {
    if (isResetPasswordSuccess) handleResetPasswordSuccess();
  }, [isResetPasswordSuccess]);

  // =========================== VERIFY CODE ===========================
  const handleResendCode = () => {
    onResendCode({ email: email.current });
  };

  // =========================== RESET PASSWORD ===========================
  const handleResetPassword = (values: FormValue, helpers: FormikHelpers<FormValue>) => {
    const { token, password } = values;
    const body = { email: email.current, password, token };
    onResetPassword(body);
  };

  const handleResetPasswordSuccess = () => {
    onShowModal({
      type: MODAL_TYPES.okModal,
      data: {
        title: <IconSuccess size={60} className="mb-12" />,
        message: 'Your password has been reset successfully!',
        okText: 'Go to Homepage',
        onOk: handleGoToHome,
      },
    });
  };

  const handleError = (error: AuthError) => {
    switch (error.code) {
      case 'CodeMismatchException':
      case 'ExpiredCodeException':
        return formRef.current.setErrors({ token: error.message });

      case 'LimitExceededException':
        return formRef.current.setErrors({ token: error.message, password: ' ' });

      default:
        return ErrorService.handler(error);
    }
  };

  const handleGoToHome = () => {
    onCloseModal();
    const signInBody = {
      username: email.current,
      password: formRef.current?.values.password,
    };

    onSignIn(signInBody);
  };

  // =========================== SCHEMA ===========================
  const ResetSchema = Yup.object().shape({
    token: Yup.string().required(),
    password: Yup.string().required(),
  });

  const isFirstTimeLogin = state.isFirstTimeLogin || false;
  return (
    <View className="ctn-uam" flexGrow={1}>
      <Image className="ctn-uam__image" src={IMAGES.backgroundLogin} />
      <View className="container" flexGrow={1}>
        <View className="ctn-uam__container" flexGrow={1}>
          <h1 className={cn('ctn-uam__title mb-8')}>{isFirstTimeLogin ? 'Welcome!' : 'Forgot Password'}</h1>
          <Text className={cn('mb-48')}>
            {isFirstTimeLogin
              ? 'Check your email and follow the instruction to set your password'
              : 'Check your email and follow the instruction to recover your password.'}
          </Text>

          <Formik
            initialValues={INTIAL}
            onSubmit={handleResetPassword}
            validationSchema={ResetSchema}
            innerRef={formRef}>
            {({ errors, touched, getFieldProps, handleSubmit, values, setValues }) => (
              <Form onSubmit={handleSubmit} autoComplete="off" className="ctn-uam__form">
                <ComfirmationCodeField
                  label="Verification Code"
                  onChange={value => setValues({ ...values, token: value })}
                  errorMessage={touched.token ? errors.token : ''}
                  containerClassName="mb-24"
                  className="ctn-uam__verify-code"
                />

                <InputPassword
                  label="New Password"
                  placeholder="Password"
                  errorMessage={touched.password ? errors.password : ''}
                  containerClassName="mb-40"
                  {...getFieldProps('password')}
                />

                <Button type="submit" className="mb-32" isLoading={loading}>
                  DONE
                </Button>
              </Form>
            )}
          </Formik>

          <View isRow align="center" className="mb-20">
            <Text className="mr-8" size={14}>
              Didn't receive the code?
            </Text>
            <Button variant="link-primary" onClick={handleResendCode}>
              RESEND CODE
            </Button>
          </View>

          <Footer className="ctn-uam__footer" />
        </View>
      </View>
    </View>
  );
};

type Props = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps & { location: Location<string> };

const mapStateToProps = (state: IRootState) => ({
  error: state.auth.error,
  loading: state.auth.loading,
  isResetPasswordSuccess: state.auth.isResetPasswordSuccess,
});

const mapDispatchToProps = {
  onSignIn: signInAsync.request,
  onResendCode: forgotPasswordAsync.request,
  onResetPassword: submitForgotPasswordAsync.request,
  onShowModal: modalActions.showModal,
  onCloseModal: modalActions.closeModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(ResetPassword);
